import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { API_URL, getAuthToken, getUserData } from '@/lib/api';
import { getLocalizedErrorMessage } from '@/lib/error-i18n';
import i18n from '@/lib/i18n';

interface Domain {
  domainName: string;
}

interface Strength {
  rank: number;
  strengthName: string;
}

interface ExtractedDataNested {
  domains: Domain[];
  strengths: Strength[];
  username: string;
}

interface ExtractedData {
  extractionId: string;
  username: string;
  userFound: boolean;
  userEmail: string | null;
  targetUserId?: string;
  fileUrl?: string;
  strengthsCount: number;
  savedRanks: number;
  extractedData: ExtractedDataNested;
}

interface ExtractResponse {
  success: boolean;
  message: string;
  data: ExtractedData;
}

export const usePdfExtract = () => {
  const [isExtracting, setIsExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const extractPdf = async (
    file: File,
    options: { targetUserId?: string } = {}
  ): Promise<{ success: boolean; data?: ExtractedData; error?: string }> => {
    setIsExtracting(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const currentUser = getUserData();
      if (currentUser?.role === 'admin' && options.targetUserId) {
        formData.append('targetUserId', options.targetUserId);
      }

      const token = getAuthToken();
      const headers: HeadersInit = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/extract`, {
        method: 'POST',
        headers,
        body: formData,
      });

      const result: ExtractResponse = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to extract PDF');
      }

      toast({
        title: i18n.t('pdf.extractionSuccess'),
        description: i18n.t('pdf.extractionSuccessDesc'),
      });

      return { success: true, data: result.data };
    } catch (err: any) {
      const errorMessage = getLocalizedErrorMessage(err, 'errors.pdfExtractFailed');
      setError(errorMessage);

      toast({
        title: i18n.t('common.error'),
        description: errorMessage,
        variant: "destructive",
      });

      return { success: false, error: errorMessage };
    } finally {
      setIsExtracting(false);
    }
  };

  return {
    extractPdf,
    isExtracting,
    error,
  };
};
